import { useSyncExternalStore } from 'react';
import { DEFAULT_QUALITY_TIER, QUALITY_TIER_OPTIONS, type QualityTier } from '@/types/models';

/**
 * The one quality tier the user builds on: picked in the home screen's input box, kept in this browser, Pro until
 * they pick another. Every start and every workspace turn reads it, so the tier is the same everywhere.
 */
const KEY = 'ivy_quality_tier';
const listeners = new Set<() => void>();
let current: QualityTier | null = null;

const isTier = (v: unknown): v is QualityTier => QUALITY_TIER_OPTIONS.some((o) => o.id === v && !o.comingSoon);

function stored(): QualityTier {
  try {
    const v = localStorage.getItem(KEY);
    if (isTier(v)) return v;
  } catch {
    // No browser storage (private window, blocked site data): the default holds until the user picks.
  }
  return DEFAULT_QUALITY_TIER;
}

export function getQualityTier(): QualityTier {
  current ??= stored();
  return current;
}

export function setQualityTier(tier: QualityTier): void {
  if (!isTier(tier)) return;
  current = tier;
  try {
    localStorage.setItem(KEY, tier);
  } catch {
    // Kept for this page only.
  }
  for (const l of listeners) l();
}

export function subscribeQualityTier(listener: () => void): () => void {
  listeners.add(listener);
  const fromOtherTab = (e: StorageEvent) => {
    if (e.key !== KEY) return;
    current = stored();
    listener();
  };
  window.addEventListener('storage', fromOtherTab);
  return () => {
    listeners.delete(listener);
    window.removeEventListener('storage', fromOtherTab);
  };
}

export function useQualityTier(): [QualityTier, (tier: QualityTier) => void] {
  return [useSyncExternalStore(subscribeQualityTier, getQualityTier), setQualityTier];
}
